import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Dashboard.css";
import { Card, CardContent, Typography, Grid, Tabs, Tab } from "@mui/material";
import { Calendar, User, Wallet } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell, Sector
} from "recharts";
import { DateRange } from "react-date-range";
import DateRangePicker from "react-bootstrap-daterangepicker";
import DateRangePickerComponent from "../../CommonComponents/DateRangePickerComponent";
import QuickAndFullRangeDatePicker from "../../CommonComponents/QuickAndFullRangeDatePicker";
import ENDPOINTS from "../../Constants/ApiEndpoints";


const COLORS = ['#ff5a7b', '#4caf50', '#9c27d8', '#1976d2', '#ffc107',];


const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />

    </g>
  );
};

const StatCard = ({ icon: Icon, value, label }) => (
  <Card className="stat-card" elevation={3}>
    <CardContent className="stat-content">
      <Icon color="#1976d2" size={32} />
      <div>
        <Typography variant="h6">{value}</Typography>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const [ipCount, setIpCount] = useState(0);
  const [inpatientsCount, setIPpatientsCount] = useState(0);
  const [collectionAmount, setCollectionAmount] = useState(0);

  const [dailyPrescriptionsData, setDailyPrescriptionsData] = useState([]);

  const [doctorMetricsData, setDoctorMetricsData] = useState([]);
  const [prescriptionsTypeMetricData, setPrescriptionsTypeMetricData] = useState([]);
  const [prescriptionsStatusMetricData, setPrescriptionsStatusMetricData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);


  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Donut Chart Radius
  const getRadius = () => {
    if (windowWidth < 480) return { inner: 90, outer: 110 };
    if (windowWidth < 768) return { inner: 150, outer: 165 };
    return { inner: 150, outer: 165 };
  };

  const { inner, outer } = getRadius();


  // Chart Width (for bar or timeline charts)
  const getResponsiveChartWidth = (dailyPrescriptionsData) => {
    if (windowWidth < 480) return Math.max(dailyPrescriptionsData * 80, 300);
    if (windowWidth < 768) return Math.max(dailyPrescriptionsData * 80, 1200);
    return Math.max(dailyPrescriptionsData * 80, 1200);
  };


  // Donut states
  const [activeIndex1, setActiveIndex1] = useState(null);





  useEffect(() => {
    const today = new Date();
    fetchMetricsFromAPI(today, today);
    fetchDailyPrescriptionCounts(today, today);
    fetchDoctorPrescriptions(today, today);
    fetchPrescriptionsTypeData(today, today);
    fetchPrescriptionsStatusData(today, today);
    //handleDateRangeChange(today, today);
  }, []);

  function formatDateToLocalYMD(date) {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  const fetchMetricsFromAPI = async (start, end) => {
    // const fromDate = start.toISOString().split("T")[0];
    // const toDate = end.toISOString().split("T")[0];

    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);
    // console.log('Raw start:', start.toString());
    // console.log('Formatted start:', formatDateToLocalYMD(start));


    try {
      const [ipRes, ippatientsRes, collectionRes] = await Promise.all([
        axios.get(ENDPOINTS.GET_TOTAL_PRESCRIPTIONS_COUNT_BY_DATE, { params: { fromDate: fromDate, toDate: toDate } }),
        axios.get(ENDPOINTS.GET_TOTAL_PATIENTS_COUNT_BY_DATE, { params: { fromDate: fromDate, toDate: toDate } }),
        axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, { params: { invoiceType: 'PRESCRIPTION_INVOICE', fromDate: fromDate, toDate: toDate } }),
      ]);

      setIpCount(ipRes.data || 0);
      setIPpatientsCount(ippatientsRes.data || 0);
      setCollectionAmount(collectionRes.data || 0);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setIpCount(0);
      setIPpatientsCount(0);
      setCollectionAmount(0);
    }
  };



  const fetchDailyPrescriptionCounts = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_DAILY_PRESCRIPTIONS_COUNT, {
        params: { fromDate, toDate },
      });

      // API returns array of { date, freeCount, paidCount }
      setDailyPrescriptionsData(response.data || []);
    } catch (error) {
      console.error("Error fetching daily prescription counts:", error);
      setDailyPrescriptionsData([]);
    }
  };


  const fetchDoctorPrescriptions = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_TOTAL_PRESCRIPTIONS_COUNT_OF_DOCTOR, {
        params: { fromDate, toDate },
      });
      setDoctorMetricsData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor prescriptions:", error);
      setDoctorMetricsData([]);
    }
  };

  const fetchPrescriptionsTypeData = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_PRESCRIPTIONS_TREND_TYPEWISE_BY_DATE, {
        params: { fromDate, toDate },
      });
      setPrescriptionsTypeMetricData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor prescriptions:", error);
      setPrescriptionsTypeMetricData([]);
    }
  };

  const fetchPrescriptionsStatusData = async (start, end) => {
    const fromDate = formatDateToLocalYMD(start);
    const toDate = formatDateToLocalYMD(end);

    try {
      const response = await axios.get(ENDPOINTS.GET_ADMISSIONS_TREND_STATUSWISEBY_DATE, {
        params: { fromDate, toDate },
      });
      setPrescriptionsStatusMetricData(response.data || []);
    } catch (error) {
      console.error("Error fetching doctor prescriptions:", error);
      setPrescriptionsStatusMetricData([]);
    }
  };


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // const handleDateRangeChange = (start, end) => {
  //   setCustomDateRange({ start, end });

  // };

  const handleDateRangeChange = (start, end, callback) => {
    //setCustomDateRange({ start, end });
    callback(start, end);
  };


  //custom CustomTooltip to avoid big rectangle highlight when hovered on bar
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '8px',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0 }}><strong>{label}</strong></p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }

    return null;
  };


  const PrescriptionCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div
          className="custom-tooltip"
          style={{
            background: '#fff',
            border: '1px solid #ccc',
            padding: '10px',
          }}
        >
          <p><strong>{label}</strong></p>
          <strong>
            <p style={{ color: '#ff5a7b' }}>Prescriptions: {data.count}</p>
          </strong>
        </div>
      );
    }
    return null;
  };






  return (
    <div className="dashboard">
      <div className="left-date-picker">
        {/* Keep your date picker logic */}
        <QuickAndFullRangeDatePicker
          // onDateChange={handleDateRangeChangeForMetrics}
          onDateChange={(start, end) => handleDateRangeChange(start, end, fetchMetricsFromAPI)}

        />

      </div>
      <Grid container spacing={3} className="dashboard-stats">
        <Grid item xs={12} md={4}>
          <StatCard icon={Calendar} value={ipCount} label="Total Prescriptions" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard icon={User} value={inpatientsCount} label="Total Patients" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard icon={Wallet} value={`₹ ${collectionAmount.toLocaleString()}`} label="Total IP Collection" />
        </Grid>
      </Grid>

      <div className="dashboard-tabs">
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Prescription Trends" />
          <Tab label="Doctor Prescription Trends" />
        </Tabs>
      </div>

      {selectedTab === 0 && (
        <div className="dashboard-chart">
          <Typography variant="h6" align="center" gutterBottom>
            Prescription Trends - Daywise
          </Typography>

          <QuickAndFullRangeDatePicker
            onDateChange={(start, end) =>
              handleDateRangeChange(start, end, fetchDailyPrescriptionCounts)
            }
          />
          <hr className="section-divider" />

          {dailyPrescriptionsData.length === 0 ? (
            <Typography
              variant="body1"
              align="center"
              style={{ marginTop: 50 }}
            >
              No Prescription data available for the selected date range.
            </Typography>
          ) : (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div
                style={{
                  width: getResponsiveChartWidth(dailyPrescriptionsData.length),
                  minHeight: 300,
                }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={dailyPrescriptionsData.map(({ date, prescriptionsCount }) => ({
                      date,
                      count: prescriptionsCount,
                     
                    }))}
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip content={<PrescriptionCustomTooltip />} cursor={{ fill: 'transparent' }} />
                    <Legend />

                    <Bar dataKey="count" stackId="a" fill="#ff5a7b" name="Prescriptions" barSize={40} style={{ cursor: 'pointer' }} />
                  </BarChart>

                </ResponsiveContainer>
              </div>
            </div>
          )}

        </div>
      )
      }

      {/* DONUT CHARTS BEGIN */}
      {selectedTab === 0 && (
        <div className="donut-row">
          <div className="donut-chart-wrapper">
            <Typography variant="h6" align="center" gutterBottom>
              Prescription Trends - Type wise
            </Typography>
            <QuickAndFullRangeDatePicker
              onDateChange={(start, end) => handleDateRangeChange(start, end, fetchPrescriptionsTypeData)}
            />
            <hr className="section-divider" />
            <div className="donut-container">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={prescriptionsTypeMetricData}
                    cx="50%"
                    cy="50%"
                    innerRadius={inner}
                    outerRadius={outer}
                    dataKey="value"
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, i) => setActiveIndex(i)}
                    onMouseLeave={() => setActiveIndex(null)}
                    paddingAngle={0}
                    cursor="pointer"
                  >
                    {prescriptionsTypeMetricData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    fontWeight="bold"
                  >
                    {activeIndex === null
                      ? `Total: ${prescriptionsTypeMetricData.reduce((sum, item) => sum + item.value, 0)}`
                      : `${prescriptionsTypeMetricData[activeIndex].name}: ${prescriptionsTypeMetricData[activeIndex].value}`}
                  </text>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* <div className="donut-chart-wrapper">
            <Typography variant="h6" align="center" gutterBottom>
              Prescription Trends - Status wise
            </Typography>
            <QuickAndFullRangeDatePicker
              onDateChange={(start, end) => handleDateRangeChange(start, end, fetchPrescriptionsStatusData)}
            />
            <hr className="section-divider" />
            <div className="donut-container">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={prescriptionsStatusMetricData}
                    cx="50%"
                    cy="50%"
                    innerRadius={inner}
                    outerRadius={outer}
                    dataKey="value"
                    activeIndex={activeIndex1}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, i) => setActiveIndex1(i)}
                    onMouseLeave={() => setActiveIndex1(null)}
                    paddingAngle={0}
                    cursor="pointer"
                  >
                    {prescriptionsStatusMetricData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    fontWeight="bold"
                  >
                    {activeIndex1 === null
                      ? `Total: ${prescriptionsStatusMetricData.reduce((sum, item) => sum + item.value, 0)}`
                      : `${prescriptionsStatusMetricData[activeIndex1].name}: ${prescriptionsStatusMetricData[activeIndex1].value}`}
                  </text>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div> */}
        </div>
      )}



      {selectedTab === 1 && (
        <div className="dashboard-chart">
          <Typography variant="h6" align="center" gutterBottom>
            Overall Doctor Prescriptions Trend
          </Typography>

          <QuickAndFullRangeDatePicker
            // onDateChange={handleDateRangeChangeForDoctorMetrics}
            onDateChange={(start, end) => handleDateRangeChange(start, end, fetchDoctorPrescriptions)}

          />

          <hr className="section-divider" />

          {doctorMetricsData.length === 0 ? (
            <Typography variant="body1" align="center" style={{ marginTop: 50 }}>
              No Prescription data available for the selected date range.
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={doctorMetricsData.map(({ doctorName, prescriptionsCount }) => ({
                  name: doctorName,
                  prescriptions: prescriptionsCount,
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                maxBarSize={50}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={20} />
                <YAxis
                  label={{
                    value: 'Total OP Prescriptions', angle: -90, position: 'insideLeft',
                    offset: 5,
                    style: { textAnchor: 'middle', fill: '#666', fontSize: 14 }
                  }}
                />

                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />

                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ paddingTop: 80, marginBottom: 20 }}
                />
                <Bar dataKey="prescriptions" fill="#3f51b5" name="Prescriptions" barSize={40} radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

    </div>

  );
};

export default Dashboard;